import { cyberpunkTitleId, cyberpunkTitleSlug } from '../../constants/cyberpunk-2077';

/** Known heatmap category keys we author in config, paint, and i18n. API keys stay `string`. */
export const HEATMAP_CATEGORY_KEYS = ['kills', 'deaths', 'successes', 'failures', 'achievements', 'general'] as const;

export type HeatmapCategoryKey = (typeof HEATMAP_CATEGORY_KEYS)[number];

export function isHeatmapCategoryKey(key: string): key is HeatmapCategoryKey {
    return (HEATMAP_CATEGORY_KEYS as readonly string[]).includes(key);
}

export type HeatmapCategoryUiOption = {
    readonly icon: 'swords' | 'skull' | 'emoji_events' | 'close' | 'star' | 'category';
    /** Marker / category accent color. */
    readonly color: string;
    /** Icon glyph color; defaults to white when omitted. */
    readonly iconColor?: string;
};

/**
 * Per-title heatmap enablement + request params.
 * Add entries here as more games get heatmap analytics.
 */
export type HeatmapTitleConfig = {
    readonly titleId: number;
    readonly titleSlug: string;
    /**
     * Optional map external ids that may show hotspots.
     * When omitted, any map for this title is eligible.
     */
    readonly mapExternalIds?: readonly string[];
    /** Category keys sent as `event_types` query param (e.g. kills, deaths). */
    readonly eventTypes: readonly HeatmapCategoryKey[];
    readonly days: number;
    /** UI chrome keyed by category. */
    readonly categoryOptions: Readonly<Partial<Record<HeatmapCategoryKey, HeatmapCategoryUiOption>>>;
};

export const HEATMAP_TITLE_CONFIGS: readonly HeatmapTitleConfig[] = [
    {
        titleId: Number(cyberpunkTitleId),
        titleSlug: cyberpunkTitleSlug,
        eventTypes: ['kills', 'deaths'],
        days: 14,
        categoryOptions: {
            kills: {
                icon: 'swords',
                color: '#7CFF3A',
                iconColor: '#0a0a0a',
            },
            deaths: {
                icon: 'skull',
                color: '#FF3D9A',
            },
        },
    },
];

export function resolveHeatmapTitleConfig({
    titleSlug,
    titleId,
    mapExternalId,
}: {
    titleSlug?: string;
    titleId?: string;
    mapExternalId?: string | null;
}): HeatmapTitleConfig | null {
    const match =
        HEATMAP_TITLE_CONFIGS.find(config => titleSlug && config.titleSlug === titleSlug) ??
        HEATMAP_TITLE_CONFIGS.find(config => titleId && String(config.titleId) === titleId) ??
        null;

    if (!match) return null;

    if (match.mapExternalIds && mapExternalId && !match.mapExternalIds.includes(mapExternalId)) {
        return null;
    }

    return match;
}

export function getHeatmapCategoryOption(
    config: HeatmapTitleConfig | null | undefined,
    categoryKey: string,
): HeatmapCategoryUiOption | undefined {
    if (!isHeatmapCategoryKey(categoryKey)) return undefined;
    return config?.categoryOptions[categoryKey];
}
